import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostListener,
	inject,
	Input,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { MoveToRootModule } from '~app-client/core/directives';
import { UiNewButtons } from '~app-ui-new/buttons';

import { BaseMenu } from '../../types/menu';

@Component({
	selector: 'popup-menu',
	templateUrl: './popup.component.html',
	styleUrls: ['./popup.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [CommonModule, UiNewButtons, MoveToRootModule],
})
export class PopupMenuComponent extends BaseMenu {
	private static readonly LEAVE_ANIMATION_MS = 100;

	private readonly cdRef = inject(ChangeDetectorRef);

	@Input('lightBg')
	public lightBg = false;

	@Input('fullHeight')
	public fullHeight = false;

	@Input()
	public width: string | null = null;

	@Input()
	public minHeight: string | null = null;

	private _animationFrameId: number | null = null;
	private _closeTimeoutId: number | null = null;
	public isClosing = false;

	private readonly visible$$ = new BehaviorSubject(false);
	public readonly visible$ = this.visible$$.asObservable();

	public open(): void {
		if (this._closeTimeoutId !== null) {
			clearTimeout(this._closeTimeoutId);
			this._closeTimeoutId = null;
		}

		this.isClosing = false;
		this.visible$$.next(true);
		this.opened$$.next();
	}

	public close(): void {
		this.isClosing = true;
		this.cdRef.detectChanges();

		if (this._closeTimeoutId !== null) {
			clearTimeout(this._closeTimeoutId);
		}

		this._closeTimeoutId = window.setTimeout(() => {
			this.visible$$.next(false);
			this.isClosing = false;
			this.closed$$.next();
			this._closeTimeoutId = null;
		}, PopupMenuComponent.LEAVE_ANIMATION_MS);
	}

	public toggle(): void {
		if (this.visible$$.value) {
			this.close();
		} else {
			this.open();
		}
	}

	@HostListener('window:resize')
	@HostListener('window:orientationchange')
	public _onResize(): void {
		if (this._animationFrameId) {
			cancelAnimationFrame(this._animationFrameId);
		}

		this._animationFrameId = requestAnimationFrame(() => {
			this.cdRef.detectChanges();
		});
	}
}
